/// <reference lib="WebWorker" />

// Require all dayjs locales
import 'dayjs/locale/en.js';
import 'dayjs/locale/es.js';
import 'dayjs/locale/de.js';
import 'dayjs/locale/fr.js';
import 'dayjs/locale/it.js';
import 'dayjs/locale/pt.js';
import { RemixBrowser, useLocation, useMatches } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import dayjs from 'dayjs';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { StrictMode, startTransition, useEffect } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { getInitialNamespaces } from 'remix-i18next/client';

import { getAppGlobals } from './components/AppGlobals/utils';
import i18n from './i18n';

// This function is called when the HUMAN captcha is successfully completed

window._pxFormId = undefined;
window._pxOnCaptchaSuccess = (isValid: boolean) => {
  if (!window._pxFormId) {
    console.log('Captcha success, but no form id found', isValid);
    return;
  }

  try {
    const form = document.getElementById(window._pxFormId) as HTMLFormElement;
    if (form) {
      const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;
      if (submitButton) {
        submitButton.click();
        console.log('Captcha success and the form was submitted', isValid);
      } else {
        console.log(`Captcha success, but form with id ${window._pxFormId} not found`, isValid);
      }
    } else {
      console.log(`Captcha success, but form with id ${window._pxFormId} not found`, isValid);
    }
  } catch (error) {
    console.log('Captcha success, but there was an error submitting the form', error, isValid);
  } finally {
    window._pxFormId = undefined;
  }
};

// add a boolean to the window object to enable logging of sentry events
// to console for debugging purposes
declare global {
  interface Window {
    __BEEHIIV_CONSOLE_LOG_SENTRY_EVENT__: boolean;
    _pxOnCaptchaSuccess: (isValid: boolean) => void;
    _pxFormId: string | undefined;
  }
}

const appGlobals = getAppGlobals();

const { retry_fetch_enabled } = getRootLoaderData();
try {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .register('/entry.worker.js', {
        type: 'module',
        scope: '/',
      })
      .then((registration) => {
        console.log(`Service Worker registered with fetcher: ${retry_fetch_enabled ? 'fetchWithRetry' : 'fetch'}`);
        registration.active?.postMessage({ type: 'update_fetcher', payload: retry_fetch_enabled });
      })
      .catch((error) => {
        // if there's an error registering the service worker, mostly due to a 403 error,
        // from Cloudflare, there's not much we can do, so we just log the error
        // and continue without the service worker
        console.error('Service Worker registration failed:', error);
      });
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data.type === 'ADD_SENTRY_BREADCRUMB') {
        Sentry.addBreadcrumb(event.data.payload);
      } else if (event.data.type === 'ADD_SENTRY_TAG') {
        Sentry.setTag(event.data.payload.key, event.data.payload.value);
      }
    });
  }
} catch (error) {
  // If the service worker registration fails, we log the error but continue
  // without the service worker. This is important for environments where service workers
  // are not supported or when the registration fails due to network issues.
  console.error('Service Worker registration failed:', error);
}

Sentry.init({
  dsn: appGlobals.SENTRY_CLIENT_DSN,
  environment: appGlobals.SENTRY_ENV,
  release: appGlobals.SENTRY_RELEASE || 'local',
  tracesSampleRate: 0, // disable performance sampling
  replaysSessionSampleRate: 0, // disable session replay
  replaysOnErrorSampleRate: 0,
  beforeBreadcrumb(breadcrumb, _hint) {
    // Filter out successful http requests
    if (breadcrumb.category === 'http' && breadcrumb.level !== 'error') {
      return null;
    }
    return breadcrumb;
  },
  beforeSend(event, _hint) {
    // Don't clog up slack sentry with development errors
    if (process.env.NODE_ENV === 'development') {
      return null;
    }

    return event;
  },

  integrations: [
    Sentry.browserTracingIntegration({
      useEffect,
      useLocation,
      useMatches,
    }),
    Sentry.replayIntegration(),
  ],
});

async function hydrate() {
  await i18next
    .use(initReactI18next) // Tell i18next to use the react-i18next plugin
    .use(LanguageDetector) // Setup a client-side language detector
    .use(Backend) // Setup your backend
    .init({
      ...i18n, // spread the configuration
      // This function detects the namespaces your routes rendered while SSR use
      ns: getInitialNamespaces(),
      backend: { loadPath: '/locales/{{lng}}/{{ns}}.json' },
      detection: {
        // Here only enable htmlTag detection, we'll detect the language only
        // server-side with remix-i18next, by using the `<html lang>` attribute
        // we can communicate to the client the language detected server-side
        order: ['htmlTag'],
        // Because we only use htmlTag, there's no reason to cache the language
        // on the browser, so we disable it
        caches: [],
      },
    });

  startTransition(() => {
    hydrateRoot(
      document,
      <I18nextProvider i18n={i18next}>
        <StrictMode>
          <RemixBrowser />
        </StrictMode>
      </I18nextProvider>,
    );
  });
}

dayjs.locale(i18next.language);

if (window.requestIdleCallback) {
  window.requestIdleCallback(hydrate);
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  window.setTimeout(hydrate, 1);
}

function getRootLoaderData() {
  // This is a HACK to get the root loader data
  // from Remix internals
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  return (window as any).__remixContext?.state?.loaderData?.root || {};
}
